import { useMemo, useState } from 'react';

import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import { PREVIEW_CONSTS, previewExampleItems } from './preview.consts';
import { useParams } from 'react-router-dom';
import { localStorageUtil } from 'utils/localStorageUtil';
import { ELocalStorageKeys } from 'constants/enums';
import { User } from 'common/contracts';
import usePublisher from 'api/usePublisher';

type ReceiptPreviewProps = {
  receiptLogo: string;
  receiptHeader: string;
};

const PreviewRow = ({
  label,
  value
}: {
  label: string;
  value: string | number;
}) => {
  return (
    <Stack direction="row" justifyContent="space-between" width="100%">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
};

export const ReceiptPreview = ({
  receiptLogo,
  receiptHeader
}: ReceiptPreviewProps) => {
  const [mode, setMode] = useState<'mobile' | 'desktop'>('mobile');
  const { publisherId } = useParams();
  const userDetails = localStorageUtil.getAny<User>(
    ELocalStorageKeys.USER_DETAILS
  );
  const { getPublisher } = usePublisher(publisherId || undefined);

  const invoiceMerchant = getPublisher.data?.invoiceMerchant || '';

  const cardWidth = useMemo(() => (mode === 'mobile' ? 360 : 480), [mode]);

  return (
    <Stack alignItems="flex-end" width="100%">
      <Stack direction="row" gap={1} mb={1}>
        <IconButton
          aria-label="mobile"
          onClick={() => setMode('mobile')}
          color={mode === 'mobile' ? 'primary' : 'default'}
          size="small"
        >
          <SmartphoneOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="desktop"
          onClick={() => setMode('desktop')}
          color={mode === 'desktop' ? 'primary' : 'default'}
          size="small"
        >
          <ViewQuiltOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Box
        sx={{
          width: cardWidth,
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          backgroundColor: '#fff'
        }}
      >
        <Box
          sx={{
            backgroundColor: receiptHeader ? 'transparent' : '#2f2f35',
            height: 160,
            width: '100%',
            overflow: 'hidden'
          }}
        >
          {receiptHeader ? (
            <Box
              component="img"
              src={receiptHeader}
              alt="header"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : null}
        </Box>
        <Box p={2}>
          <Typography fontWeight={700} mb={1.5} fontSize={16}>
            Purchase Confirmation
          </Typography>
          <Stack direction="row" alignItems="center" gap={1.5} mb={1.5}>
            <Box
              component="img"
              src={receiptLogo}
              alt="logo"
              sx={{
                width: 24,
                height: 24,
                borderRadius: '4px',
                objectFit: 'contain'
              }}
            />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {invoiceMerchant}
            </Typography>
          </Stack>
          <Stack gap={1}>
            <PreviewRow label="Receipt Number" value="602-758-041" />
            <PreviewRow label="Payment Method" value="Visa (1111)" />
            <PreviewRow
              label="Payment Date"
              value="November 13, 2025 3:21 PM UTC"
            />
            <PreviewRow label="Country" value="Israel" />
            <PreviewRow label="Order ID" value="6915f7714cd9f675fd17df84" />
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body2" fontWeight={700} gutterBottom>
            Items
          </Typography>
          <Stack gap={1.25}>
            {previewExampleItems.map((item) => (
              <Stack
                key={item.label}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Stack direction="row" alignItems="center" gap={1.25}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: item.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16
                    }}
                  >
                    {item.emoji}
                  </Box>
                  <Typography variant="body2">{item.label}</Typography>
                </Stack>
                <Typography variant="body2">{item.qty}</Typography>
              </Stack>
            ))}
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" fontWeight={700}>
              Subtotal
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              ${PREVIEW_CONSTS.SUBTOTAL}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={0.75}
          >
            <Typography variant="body2" fontWeight={700}>
              Tax (18%)
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              ${PREVIEW_CONSTS.TAX}
            </Typography>
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" fontWeight={700}>
              Total
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              ${PREVIEW_CONSTS.TOTAL}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
};

export default ReceiptPreview;
